import { User } from '../context/AuthContext'
import { getBackendUrl } from '../env'

export type NotificationStatus = 'active' | 'acknowledged' | 'resolved'
/** `open` = active + acknowledged + legacy rows with no status (needs attention). */
export type NotificationListStatus = NotificationStatus | 'open'
export type NotificationSeverity = 'info' | 'warning' | 'critical' | 'error'

export type NotificationItem = {
  _id: string
  message: string
  type?: string
  severity?: NotificationSeverity
  status?: NotificationStatus
  device_id?: string | null
  system_id?: string | null
  alert_key?: string | null
  eventTime: string
  createdAt: string
  updatedAt: string
  acknowledgedAt?: string
  resolvedAt?: string
}

export type ListNotificationsParams = {
  status?: NotificationListStatus
  severity?: NotificationSeverity
  type?: string
  device_id?: string
  system_id?: string
  /** ISO 8601 — only notifications updated on or after this instant. */
  since?: string
  page?: number
  limit?: number
}

export type ListNotificationsResponse = {
  items: NotificationItem[]
  page: number
  limit: number
  total: number
}

const buildQuery = (params?: ListNotificationsParams): string => {
  const query = new URLSearchParams()
  if (!params) return query.toString()
  if (params.status) query.set('status', params.status)
  if (params.severity) query.set('severity', params.severity)
  if (params.type) query.set('type', params.type)
  if (params.device_id) query.set('device_id', params.device_id)
  if (params.system_id) query.set('system_id', params.system_id)
  if (params.since) query.set('since', params.since)
  if (params.page != null) query.set('page', String(params.page))
  if (params.limit != null) query.set('limit', String(params.limit))
  return query.toString()
}

export const getNotifications = async (
  user: User,
  params?: ListNotificationsParams
): Promise<ListNotificationsResponse | { error: unknown }> => {
  try {
    const query = buildQuery(params)
    const endpoint = `${getBackendUrl()}/api/notifications` + (query ? `?${query}` : '')
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.token}`,
      },
    })
    const json = await response.json()
    if (response.ok) {
      return json as ListNotificationsResponse
    }
    return { error: json }
  } catch (error) {
    return { error }
  }
}

export const acknowledgeNotification = async (
  user: User,
  notificationId: string
): Promise<NotificationItem | { error: unknown }> => {
  try {
    const response = await fetch(`${getBackendUrl()}/api/notifications/${notificationId}/acknowledge`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.token}`,
      },
    })
    const json = await response.json()
    if (response.ok) {
      return json as NotificationItem
    }
    return { error: json }
  } catch (error) {
    return { error }
  }
}

export const resolveNotification = async (
  user: User,
  notificationId: string
): Promise<NotificationItem | { error: unknown }> => {
  try {
    const response = await fetch(`${getBackendUrl()}/api/notifications/${notificationId}/resolve`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.token}`,
      },
    })
    const json = await response.json()
    if (response.ok) {
      return json as NotificationItem
    }
    return { error: json }
  } catch (error) {
    return { error }
  }
}